import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChevronRight, Sparkles } from "lucide-react";

// Guide steps configuration - exactly 4 steps
const GUIDE_STEPS = [
  {
    id: "safe-to-spend",
    targetSelector: '[data-guide="safe-to-spend"]',
    title: "Safe to Spend",
    text: "This is what you can safely spend this week",
    position: "bottom"
  },
  {
    id: "alerts",
    targetSelector: '[data-guide="alerts"]',
    fallbackSelector: '[data-guide="survival-toggle"]',
    secondarySelector: '[data-guide="survival-toggle"]',
    title: "Smart Alerts",
    text: "We warn you before you run short — turn on Survival Mode for essentials only",
    position: "bottom"
  },
  {
    id: "calendar",
    targetSelector: '[data-guide="calendar"]',
    title: "Your Calendar",
    text: "See exactly when money goes in and out",
    position: "top"
  },
  {
    id: "add-button",
    targetSelector: '[data-guide="add-button"]',
    title: "Add Transactions",
    text: "Add income or bills here",
    position: "left"
  }
];

// Check if element is valid for highlighting (visible and has proper dimensions)
function isElementValid(element, minWidth = 120, minHeight = 40) {
  if (!element) return false;
  
  // Check computed style for visibility
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  
  const rect = element.getBoundingClientRect();
  
  // Strict validation: Element must meet minimum size requirements
  // This prevents targeting collapsed containers or invisible wrappers
  const isValidSize = rect.width >= minWidth && rect.height >= minHeight;
  const isInViewport = rect.top > -1000 && rect.top < window.innerHeight + 1000;
  
  return isValidSize && isInViewport;
}

// Spotlight Overlay Component with optional secondary highlight
function SpotlightOverlay({ targetRect, secondaryRect }) {
  if (!targetRect) return null;

  const padding = 8;
  const borderRadius = 12;

  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      <svg className="w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {/* Primary spotlight cutout */}
            <rect
              x={targetRect.left - padding}
              y={targetRect.top - padding}
              width={targetRect.width + padding * 2}
              height={targetRect.height + padding * 2}
              rx={borderRadius}
              ry={borderRadius}
              fill="black"
            />
            {/* Secondary spotlight cutout (lighter) - only if different from primary */}
            {secondaryRect && (
              Math.abs(secondaryRect.top - targetRect.top) > 20 || 
              Math.abs(secondaryRect.left - targetRect.left) > 20
            ) && (
              <rect
                x={secondaryRect.left - 4}
                y={secondaryRect.top - 4}
                width={secondaryRect.width + 8}
                height={secondaryRect.height + 8}
                rx={8}
                ry={8}
                fill="black"
                fillOpacity="0.7"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.65)"
          mask="url(#spotlight-mask)"
        />
      </svg>
      
      {/* Primary glowing border around highlighted element */}
      <div
        className="absolute pointer-events-none transition-all duration-300"
        style={{
          left: targetRect.left - padding - 2,
          top: targetRect.top - padding - 2,
          width: targetRect.width + padding * 2 + 4,
          height: targetRect.height + padding * 2 + 4,
          borderRadius: borderRadius + 2,
          border: "2px solid rgba(16, 185, 129, 0.6)",
          boxShadow: "0 0 20px rgba(16, 185, 129, 0.4), inset 0 0 20px rgba(16, 185, 129, 0.1)"
        }}
      />
      
      {/* Secondary subtle pulse highlight - only if visible and different location */}
      {secondaryRect && (
        Math.abs(secondaryRect.top - targetRect.top) > 20 || 
        Math.abs(secondaryRect.left - targetRect.left) > 20
      ) && (
        <div
          className="absolute pointer-events-none animate-pulse transition-all duration-300"
          style={{
            left: secondaryRect.left - 6,
            top: secondaryRect.top - 6,
            width: secondaryRect.width + 12,
            height: secondaryRect.height + 12,
            borderRadius: 10,
            border: "1.5px dashed rgba(251, 191, 36, 0.6)",
            boxShadow: "0 0 12px rgba(251, 191, 36, 0.25)"
          }}
        />
      )}
    </div>
  );
}

// Tooltip Component - anchored only to primary target
function GuideTooltip({ step, targetRect, currentStep, totalSteps, onNext, onSkip, onComplete }) {
  if (!targetRect || !step) return null;

  const isLastStep = currentStep === totalSteps - 1;
  
  // Calculate tooltip position
  const tooltipWidth = 300;
  const tooltipHeight = 150;
  const offset = 14;
  
  let left, top;
  
  switch (step.position) {
    case "top":
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      top = targetRect.top - tooltipHeight - offset;
      break;
    case "bottom":
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      top = targetRect.bottom + offset;
      break;
    case "left":
      left = targetRect.left - tooltipWidth - offset;
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      break;
    case "right":
      left = targetRect.right + offset;
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      break;
    default:
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      top = targetRect.bottom + offset;
  }

  // Keep tooltip within viewport with padding
  const viewportPadding = 16;
  left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipWidth - viewportPadding));
  top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipHeight - viewportPadding));

  return (
    <div
      className="fixed z-[9999] animate-in fade-in slide-in-from-bottom-2 duration-300"
      style={{ left, top, width: tooltipWidth }}
      data-testid="guide-tooltip"
    >
      <div className="bg-slate-900 rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
        {/* Progress indicator */}
        <div className="h-1 bg-slate-800">
          <div 
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>
        
        <div className="p-4">
          {/* Step indicator */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">
              Step {currentStep + 1} of {totalSteps}
            </span>
            <button
              onClick={onSkip}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
              data-testid="guide-skip-btn"
            >
              Skip
            </button>
          </div>
          
          {/* Content */}
          <p className="text-white text-sm leading-relaxed mb-4">
            {step.text}
          </p>
          
          {/* Actions */}
          <div className="flex justify-end">
            <Button
              onClick={isLastStep ? onComplete : onNext}
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm h-9 px-4 rounded-lg"
              data-testid="guide-next-btn"
            >
              {isLastStep ? (
                <>
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Done
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Guide Component
export default function GuidedHighlight({ forceShow = false, onComplete: onCompleteCallback }) {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [secondaryRect, setSecondaryRect] = useState(null);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const updateTimeoutRef = useRef(null);
  const positionUpdateRef = useRef(null);

  // Track event helper (defined first so it can be used elsewhere)
  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    try {
      await axios.post(`${API}/events/track`, {
        event_name: eventName,
        metadata
      }, { withCredentials: true });
    } catch (error) {
      // Silent fail for tracking
    }
  }, []);

  // Mark guide as complete in database
  const markGuideComplete = useCallback(async () => {
    try {
      await axios.patch(`${API}/users/settings`, {
        guide_completed: true
      }, { withCredentials: true });
      
      if (user?.user_id) {
        localStorage.setItem(`guide_completed_${user.user_id}`, "true");
      }
    } catch (error) {
      if (user?.user_id) {
        localStorage.setItem(`guide_completed_${user.user_id}`, "true");
      }
    }
  }, [user?.user_id]);

  // Handle complete
  const handleComplete = useCallback(async () => {
    trackEvent("guide_completed", { total_steps: GUIDE_STEPS.length });
    
    setIsActive(false);
    setCurrentStep(0);
    setTargetRect(null);
    setSecondaryRect(null);
    
    await markGuideComplete();
    
    toast.success("You're in control this week — try Survival Mode anytime", {
      duration: 3000,
      icon: <Sparkles className="w-5 h-5 text-emerald-400" />
    });
    
    onCompleteCallback?.();
  }, [trackEvent, markGuideComplete, onCompleteCallback]);

  // Handle skip
  const handleSkip = useCallback(async () => {
    trackEvent("guide_skipped", { skipped_at_step: currentStep + 1 });
    
    setIsActive(false);
    setCurrentStep(0);
    setTargetRect(null);
    setSecondaryRect(null);
    
    await markGuideComplete();
    onCompleteCallback?.();
  }, [currentStep, trackEvent, markGuideComplete, onCompleteCallback]);

  // Handle next step
  const handleNext = useCallback(() => {
    const step = GUIDE_STEPS[currentStep];
    trackEvent("guide_step_viewed", {
      step_number: currentStep + 1,
      step_name: step?.id
    });
    
    if (currentStep < GUIDE_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, trackEvent]);

  // Check if guide should show
  useEffect(() => {
    if (forceShow) {
      setIsActive(true);
      setCurrentStep(0);
      trackEvent("guide_started", { source: "manual_replay" });
      return;
    }

    const checkGuideStatus = async () => {
      if (!user || hasCheckedStatus) return;
      
      try {
        if (user.onboarding_completed && !user.guide_completed) {
          const localGuideComplete = localStorage.getItem(`guide_completed_${user.user_id}`);
          if (!localGuideComplete) {
            setTimeout(() => {
              setIsActive(true);
              trackEvent("guide_started", { source: "auto" });
            }, 1000);
          }
        }
      } catch (error) {
        // Silent fail
      }
      
      setHasCheckedStatus(true);
    };

    checkGuideStatus();
  }, [user, forceShow, hasCheckedStatus, trackEvent]);

  // Update target position - extracted function for resize/scroll handlers
  const updateTargetPositions = useCallback(() => {
    const step = GUIDE_STEPS[currentStep];
    if (!step) return false;

    // Find primary target element with strict validation
    let element = document.querySelector(step.targetSelector);
    
    // Check if primary element passes strict validation
    if (!isElementValid(element, 120, 40)) {
      // Try fallback selector if available
      if (step.fallbackSelector) {
        const fallbackElement = document.querySelector(step.fallbackSelector);
        if (isElementValid(fallbackElement, 120, 40)) {
          element = fallbackElement;
        }
      }
    }
    
    // If still no valid element, return false to indicate failure
    if (!isElementValid(element, 120, 40)) {
      return false;
    }
    
    // Get fresh rect
    const rect = element.getBoundingClientRect();
    setTargetRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
      right: rect.right
    });
    
    // Check for secondary element with relaxed validation
    if (step.secondarySelector) {
      const secondaryElement = document.querySelector(step.secondarySelector);
      if (secondaryElement && isElementValid(secondaryElement, 80, 30)) {
        const secRect = secondaryElement.getBoundingClientRect();
        // Only show secondary if it's in a different position than primary
        const isDifferentPosition = 
          Math.abs(secRect.top - rect.top) > 20 || 
          Math.abs(secRect.left - rect.left) > 20;
        
        if (isDifferentPosition) {
          setSecondaryRect({
            top: secRect.top,
            left: secRect.left,
            width: secRect.width,
            height: secRect.height
          });
        } else {
          setSecondaryRect(null);
        }
      } else {
        setSecondaryRect(null);
      }
    } else {
      setSecondaryRect(null);
    }
    
    return true;
  }, [currentStep]);

  // Update target rect when step changes
  useEffect(() => {
    if (!isActive) return;

    let retryCount = 0;
    const maxRetries = 3; // Strict: only 3 retries as per spec

    const scrollAndUpdate = async () => {
      const step = GUIDE_STEPS[currentStep];
      if (!step) return;

      // Find and validate primary target element
      let element = document.querySelector(step.targetSelector);
      let usingFallback = false;
      
      // Check if primary element passes strict validation (minWidth=120, minHeight=40)
      if (!isElementValid(element, 120, 40)) {
        // Try fallback selector if available
        if (step.fallbackSelector) {
          const fallbackElement = document.querySelector(step.fallbackSelector);
          if (isElementValid(fallbackElement, 120, 40)) {
            element = fallbackElement;
            usingFallback = true;
          }
        }
      }
      
      // If still no valid element found, retry with 200ms delay (max 3 retries)
      if (!isElementValid(element, 120, 40)) {
        retryCount++;
        if (retryCount < maxRetries) {
          clearTimeout(updateTimeoutRef.current);
          updateTimeoutRef.current = setTimeout(scrollAndUpdate, 200);
          return;
        }
        
        // Max retries reached - skip to next step
        if (currentStep < GUIDE_STEPS.length - 1) {
          setCurrentStep(prev => prev + 1);
        } else {
          handleComplete();
        }
        return;
      }
      
      // Clear previous rect while transitioning
      setTargetRect(null);
      setSecondaryRect(null);
      
      // Scroll element into view ONLY after validation passes
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      
      // Wait 300ms for scroll to complete, then recalculate rect
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = setTimeout(() => {
        // Use double requestAnimationFrame to ensure DOM has fully settled
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Re-validate after scroll
            const finalElement = usingFallback 
              ? document.querySelector(step.fallbackSelector)
              : document.querySelector(step.targetSelector);
            
            if (!isElementValid(finalElement, 120, 40)) {
              // Post-scroll validation failed, try one more time
              if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(scrollAndUpdate, 200);
                return;
              }
            }
            
            // Get fresh bounding rect after scroll
            const rect = finalElement.getBoundingClientRect();
            
            setTargetRect({
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              bottom: rect.bottom,
              right: rect.right
            });
            
            // Handle secondary element (e.g., survival toggle)
            if (step.secondarySelector && !usingFallback) {
              const secondaryElement = document.querySelector(step.secondarySelector);
              if (secondaryElement && isElementValid(secondaryElement, 80, 30)) {
                const secRect = secondaryElement.getBoundingClientRect();
                // Only show if different position from primary
                const isDifferent = 
                  Math.abs(secRect.top - rect.top) > 20 || 
                  Math.abs(secRect.left - rect.left) > 20;
                
                if (isDifferent) {
                  setSecondaryRect({
                    top: secRect.top,
                    left: secRect.left,
                    width: secRect.width,
                    height: secRect.height
                  });
                } else {
                  setSecondaryRect(null);
                }
              } else {
                setSecondaryRect(null);
              }
            } else {
              setSecondaryRect(null);
            }
          });
        });
      }, 300); // Wait 300ms after scroll as per spec
    };

    // Initial delay to let the page render
    const initialDelay = setTimeout(scrollAndUpdate, 150);
    
    // Update on resize
    const handleResize = () => {
      clearTimeout(positionUpdateRef.current);
      positionUpdateRef.current = setTimeout(() => {
        requestAnimationFrame(() => {
          updateTargetPositions();
        });
      }, 100);
    };
    
    // Update on scroll (debounced)
    const handleScroll = () => {
      clearTimeout(positionUpdateRef.current);
      positionUpdateRef.current = setTimeout(() => {
        requestAnimationFrame(() => {
          updateTargetPositions();
        });
      }, 50);
    };
    
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(updateTimeoutRef.current);
      clearTimeout(positionUpdateRef.current);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isActive, currentStep, handleComplete, updateTargetPositions]);

  // Don't render if not active or no valid target
  if (!isActive) return null;

  const currentStepData = GUIDE_STEPS[currentStep];

  return createPortal(
    <>
      {targetRect && (
        <>
          <SpotlightOverlay 
            targetRect={targetRect}
            secondaryRect={currentStepData?.secondarySelector ? secondaryRect : null}
          />
          <GuideTooltip
            step={currentStepData}
            targetRect={targetRect}
            currentStep={currentStep}
            totalSteps={GUIDE_STEPS.length}
            onNext={handleNext}
            onSkip={handleSkip}
            onComplete={handleComplete}
          />
        </>
      )}
    </>,
    document.body
  );
}

// Hook to trigger guide replay
export function useGuideReplay() {
  const [showGuide, setShowGuide] = useState(false);

  const replayGuide = useCallback(() => {
    setShowGuide(true);
  }, []);

  const closeGuide = useCallback(() => {
    setShowGuide(false);
  }, []);

  return { showGuide, replayGuide, closeGuide };
}
